<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getCompetition, validateVessel, submitVessel, type Competition } from '../../api/competitions'
import type { ApiError } from '../../api/client'
import CInput from '../../components/CInput.vue'
import CButton from '../../components/CButton.vue'
import GFormErrors from '../../components/GFormErrors.vue'

const route = useRoute()
const router = useRouter()

const competition = ref<Competition | null>(null)
const vesselName = ref('')
const craftFile = ref<File | null>(null)
const errors = ref<{ msg: string; path?: string }[]>([])
const busy = ref(false)

type Step = 'form' | 'confirm'
const step = ref<Step>('form')
const validationErrors = ref<string[]>([])
const validationWarnings = ref<string[]>([])

onMounted(async () => {
	competition.value = await getCompetition(route.params.id as string)
})

function onFileChange(e: Event) {
	craftFile.value = (e.target as HTMLInputElement).files?.[0] ?? null
}

function backToForm() {
	step.value = 'form'
	validationErrors.value = []
	validationWarnings.value = []
	errors.value = []
}

async function validate() {
	errors.value = []

	if (!craftFile.value) {
		errors.value = [{ msg: 'Please select a .craft file' }]
		return
	}

	busy.value = true

	try {
		const result = await validateVessel(route.params.id as string, craftFile.value)
		validationErrors.value = result.errors
		validationWarnings.value = result.warnings
		step.value = 'confirm'
	} catch (e) {
		errors.value = (e as ApiError).errors ?? [{ msg: String(e) }]
	} finally {
		busy.value = false
	}
}

async function submit() {
	errors.value = []
	busy.value = true

	try {
		await submitVessel(route.params.id as string, vesselName.value, craftFile.value!)
		router.push(`/competitions/${route.params.id}`)
	} catch (e) {
		errors.value = (e as ApiError).errors ?? [{ msg: String(e) }]
		step.value = 'form'
	} finally {
		busy.value = false
	}
}
</script>

<template>
	<main class="l-row l-stack">
		<GFormErrors :errors="errors" />

		<h1>{{ competition?.name }}</h1>

		<form v-if="step === 'form'" class="l-stack" @submit.prevent="validate">
			<h2>Submit a Craft</h2>

			<p>Let us know the details of your vessel below.</p>

			<CInput v-model="vesselName" name="name" type="text" title="Vessel Name" :wide="true" :required="true" />

			<input type="file" name="craft_file" style="display: block;" accept=".craft" required @change="onFileChange" />

			<CButton type="submit" :disabled="busy" class="c-button--success">Validate &amp; Continue</CButton>
			<RouterLink :to="`/competitions/${competition?.id}`" class="c-button">Cancel</RouterLink>
		</form>

		<template v-else-if="step === 'confirm'">
			<h2>Confirm Submission</h2>

			<template v-if="validationErrors.length">
				<p><strong>Your craft has the following errors and cannot be submitted:</strong></p>
				<ul>
					<li v-for="(e, i) in validationErrors" :key="i">{{ e }}</li>
				</ul>
				<CButton @click="backToForm">Back</CButton>
			</template>

			<template v-else-if="validationWarnings.length">
				<p><strong>Your craft has the following advisories:</strong></p>
				<ul>
					<li v-for="(w, i) in validationWarnings" :key="i">{{ w }}</li>
				</ul>
				<p>Some of these might be false positives, so if your craft looks ok in KSP, it's probably fine. We manually check all submissions either way.</p>
				<p>However, if your craft is actually in violation of the rules, it may be banished to the Shadow Realm (or we'll reach out and politely ask you to fix it).</p>
				<div class="l-stack-horizontal">
					<CButton @click="backToForm" :disabled="busy">Back</CButton>
					<CButton @click="submit" :disabled="busy" class="c-button--danger">Submit Anyway</CButton>
				</div>
			</template>

			<template v-else>
				<p>All validation checks passed.</p>
				<p>Note that we'll still manually check all submissions.</p>
				<div class="l-stack-horizontal">
					<CButton @click="backToForm" :disabled="busy">Back</CButton>
					<CButton @click="submit" :disabled="busy" class="c-button--success">Submit</CButton>
				</div>
			</template>
		</template>
	</main>
</template>
